"use client";
import Link from "next/link";
import { usePathname, useParams, useRouter } from "next/navigation";
import { useEffect, useRef, useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { getToken, clearToken } from "@/lib/api/client";
import { useGetFavoriteReceivedUnread, useGetFavoriteReceivedUnreadCount, useNotificationList, useMessageConversations } from "@/lib/api/hooks";
import { markAllFavoriteReceivedRead, getMessageUnreadSummary, listingApi } from "@/lib/api/listings";
import { useI18n } from "@/i18n";
import { useLangHref } from "@/i18n/useLangHref";
import { DEFAULT_LANG, LANG_PREFIX } from "@/i18n/constants";
import LoginModal from "./LoginModal";

const languages = [
    { code: "en", flag: "EN", name: "English" },
    { code: "fr", flag: "FR", name: "Français" },
];

export default function Header() {
    const { t, lang } = useI18n();
    const langHref = useLangHref();
    const router = useRouter();
    const pathname = usePathname();
    const params = useParams();

    // navLinks 改为 useMemo 生成：label 用 t()，href 用 langHref() 保持语言前缀
    const navLinks = useMemo(() => [
        { href: langHref("/"), label: t("nav.home"), key: "home" },
        { href: langHref("/search"), label: t("nav.buy"), key: "buy" },
        { href: langHref("/parts"), label: t("nav.parts"), key: "parts" },
        { href: langHref("/dealers"), label: t("nav.dealers"), key: "dealers" },
        { href: langHref("/blog"), label: t("nav.blog"), key: "blog" },
    ], [t, langHref]);

    const [langOpen, setLangOpen] = useState(false);
    const [favOpen, setFavOpen] = useState(false);
    const [ntfOpen, setNtfOpen] = useState(false);
    const [ntfTab, setNtfTab] = useState("message");
    const [mobileOpen, setMobileOpen] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);
    const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
    const [searchVal, setSearchVal] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggest, setShowSuggest] = useState(false);
    const [suggestActiveIdx, setSuggestActiveIdx] = useState(-1);
    const suggestTimerRef = useRef(null);
    const searchBarRef = useRef(null);
    const [logoutLoading, setLogoutLoading] = useState(false);
    const [userRefreshTick, setUserRefreshTick] = useState(0);  // 监听 auth:user-updated 后 +1 触发重渲染

    // === 未读总数（notification badge）：自己 fetch + 5min 轮询 ===
    const [ntfBadge, setNtfBadge] = useState(0);
    useEffect(() => {
        if (!isLoggedIn) { setNtfBadge(0); return; }
        let cancelled = false;
        const pull = async () => {
            try {
                const res = await getMessageUnreadSummary();
                if (cancelled) return;
                // apiClient 已解包 {code,message,data} → res = { success, data }
                // 后端 data: { notificationUnread, total, messageUnread }
                const total = res?.data?.total ?? 0;
                setNtfBadge(Number(total) || 0);
            } catch (e) { /* 静默失败 */ }
        };
        pull();
        const timer = setInterval(pull, 300000);  // 5 分钟
        return () => { cancelled = true; clearInterval(timer); };
    }, [isLoggedIn]);

    // === 收藏 count（enabled=false 不自动请求，用 5s 定时器 + 触发式 refetch） ===
    const { data: favCountData, refetch: refetchFavCount } = useGetFavoriteReceivedUnreadCount({ enabled: false });
    const rawFavCount = favCountData?.data ?? favCountData?.count ?? favCountData ?? 0;

    // 5min 轮询：登录后首次立即拉一次，之后每 5min 自动刷新 count；未登录不请求
    useEffect(() => {
        if (!isLoggedIn) return;
        refetchFavCount?.();
        const timer = setInterval(() => refetchFavCount?.(), 300000);
        return () => clearInterval(timer);
    }, [refetchFavCount, isLoggedIn]);

    // === 收藏列表（弹窗内展示 + 自管理分页） ===
    const [favPage, setFavPage] = useState(1);
    const favSize = 5;
    const [favRefreshTick, setFavRefreshTick] = useState(0);
    const favBodyRef = useRef(null);

    // 每次打开弹窗 → 重置到第 1 页 + tick+1 强制重新请求
    useEffect(() => {
        if (favOpen) {
            setFavPage(1);
            setFavRefreshTick((k) => k + 1);
        }
    }, [favOpen]);

    const favParams = { pageNum: favPage, pageSize: favSize, tick: favRefreshTick };
    const {
        data: favData,
        isLoading: favListLoading,
        isFetching: favListFetching,
    } = useGetFavoriteReceivedUnread(favParams, favOpen);
    const favList = favData?.data?.records || favData?.data?.list || favData?.data || favData || [];
    const favRecords = Array.isArray(favList) ? favList : [];
    const favPagination = favData?.data?.pagination || favData?.pagination;
    const favTotal = favPagination?.total ?? favData?.total ?? null;
    const favListHasMore = favTotal != null
        ? (favPage * favSize < favTotal)
        : favRecords.length >= favSize;
    const favListLoadingMore = favListFetching && favPage > 1;

    // 列表数据到达（任意页）→ 立即触发一次 count 刷新
    useEffect(() => {
        if (favOpen && favData) {
            refetchFavCount?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [favData]);

    // 弹窗内列表滚到底部 → 拉下一页
    const handleFavBodyScroll = (e) => {
        const el = e.target;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24
            && favListHasMore
            && !favListLoadingMore
            && !favListLoading) {
            setFavPage((p) => p + 1);
        }
    };

    // === 徽章数字 ===
    // 关闭弹窗：直接用定时器拉的最新 count
    // 打开弹窗：count 减去列表已展示条数（后端返回列表时自动标记已读）
    const badgeCount = favOpen && favRecords.length > 0
        ? Math.max(0, rawFavCount - favRecords.length)
        : rawFavCount;

    // === 关闭弹窗（纯关，不触发任何 refetch） ===
    const closeFavPopover = () => {
        setFavOpen(false);
    };

    // "全部已读" 按钮：成功后立刻 refetch count
    const [markReadLoading, setMarkReadLoading] = useState(false);
    const [markReadToast, setMarkReadToast] = useState(null);
    const markReadToastTimerRef = useRef(null);
    const showMarkReadToast = (msg) => {
        if (markReadToastTimerRef.current) clearTimeout(markReadToastTimerRef.current);
        setMarkReadToast(msg);
        markReadToastTimerRef.current = setTimeout(() => setMarkReadToast(null), 2500);
    };
    const handleMarkAllRead = async () => {
        if (favRecords.length === 0) {
            showMarkReadToast(t("header.noFavoritesToMark"));
            return;
        }
        setMarkReadLoading(true);
        try {
            await markAllFavoriteReceivedRead();
            showMarkReadToast(t("header.markAllReadSuccess"));
            refetchFavCount?.();   // 成功后立马刷新 count
            setFavOpen(false);
        } catch (err) {
            showMarkReadToast(err?.message || t("header.markAllReadFail"));
        } finally {
            setMarkReadLoading(false);
        }
    };
    const [ntfRefreshTick, setNtfRefreshTick] = useState(0);
    // 系统通知列表（上拉加载更多）
    const {
        list: ntfSystemList,
        loading: ntfSystemLoading,
        loadingMore: ntfSystemLoadingMore,
        hasMore: ntfSystemHasMore,
        loadMore: ntfSystemLoadMore,
        reset: ntfSystemReset,
    } = useNotificationList({ enabled: ntfOpen && ntfTab === "system", pageSize: 10, refreshKey: ntfRefreshTick });
    // 消息会话列表（完整拉取，不分页）
    const {
        data: ntfMsgData,
        isLoading: ntfMsgLoading,
    } = useMessageConversations({ enabled: ntfOpen && ntfTab === "message", refreshKey: ntfRefreshTick });
    const ntfMsgList = ntfMsgData?.data?.records
        || ntfMsgData?.records
        || ntfMsgData?.data
        || ntfMsgData
        || [];
    const ntfMsgArr = Array.isArray(ntfMsgList) ? ntfMsgList : [];
    // 切换 tab 时：系统重置 + tick+1（当前 tab 的数据 hook 会重新请求）
    useEffect(() => {
        ntfSystemReset();
        setNtfRefreshTick((t) => t + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ntfTab]);
    // 每次打开弹窗：重置到"私信"tab + tick+1（所有 tab 数据都强制重新请求）
    const prevNtfOpenRef = useRef(false);
    useEffect(() => {
        if (ntfOpen && !prevNtfOpenRef.current) {
            setNtfTab("message");
            setNtfRefreshTick((t) => t + 1);
        }
        prevNtfOpenRef.current = ntfOpen;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ntfOpen]);
    // 多语言字段映射：API 返回 titleZh/titleEn/titleFr/contentZh/...
    const unescapeText = (s) => {
        if (!s) return "";
        // 去掉后端转义的反斜杠：\" → "  \\ → \  \n → 换行
        return String(s)
            .replace(/\\n/g, "\n")
            .replace(/\\r/g, "")
            .replace(/\\t/g, "\t")
            .replace(/\\"/g, '"')
            .replace(/\\'/g, "'")
            .replace(/\\\\/g, "\\");
    };
    const getNotificationTitle = (n) => {
        const langKey = lang?.toUpperCase() === "FR" ? "Fr" : (lang?.toUpperCase() === "EN" ? "En" : "Zh");
        return unescapeText(n[`title${langKey}`] || n.titleEn || n.titleZh || n.titleFr || "");
    };
    const getNotificationContent = (n) => {
        const langKey = lang?.toUpperCase() === "FR" ? "Fr" : (lang?.toUpperCase() === "EN" ? "En" : "Zh");
        return unescapeText(n[`content${langKey}`] || n.contentEn || n.contentZh || n.contentFr || "");
    };
    // popover-body scroll ref：触底加载下一页
    const ntfBodyRef = useRef(null);
    const handleNtfBodyScroll = (e) => {
        const el = e.target;
        if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20 && ntfSystemHasMore && !ntfSystemLoadingMore) {
            ntfSystemLoadMore();
        }
    };

    // 日期格式化：yyyy-MM-dd HH:mm:ss
    const formatDateTime = (t) => {
        if (!t) return "";
        const d = new Date(t);
        if (isNaN(d.getTime())) return t; // 无法解析原样返回
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    const accountMenuRef = useRef(null);
    const closeTimerRef = useRef(null);
    const langRef = useRef(null);
    const favRef = useRef(null);
    const ntfRef = useRef(null);
    useEffect(() => {
        setIsLoggedIn(!!getToken());
        function onAuthChange() { setIsLoggedIn(!!getToken()); }
        function onUserUpdated() { setUserRefreshTick((t) => t + 1); }
        window.addEventListener("storage", onAuthChange);
        window.addEventListener("auth:unauthorized", onAuthChange);
        window.addEventListener("auth:user-updated", onUserUpdated);
        // 检测 URL 参数 ?login=1，自动打开登录弹窗
        const params = new URLSearchParams(window.location.search);
        if (params.get("login") === "1") {
            setLoginModalOpen(true);
            // 清除 URL 参数，刷新 URL 不留痕迹
            const url = new URL(window.location.href);
            url.searchParams.delete("login");
            window.history.replaceState({}, "", url.toString());
        }
        return () => {
            window.removeEventListener("storage", onAuthChange);
            window.removeEventListener("auth:unauthorized", onAuthChange);
            window.removeEventListener("auth:user-updated", onUserUpdated);
        };
    }, [pathname]);
    // 路由变化时关闭所有弹出层（客户端跳转不重建组件，state 会残留）
    useEffect(() => {
        setLangOpen(false);
        setFavOpen(false);
        setNtfOpen(false);
        setAccountMenuOpen(false);
    }, [pathname]);
    useEffect(() => {
        function handleClickOutside(e) {
            const target = e.target;
            if (langRef.current && !langRef.current.contains(target))
                setLangOpen(false);
            if (favRef.current && !favRef.current.contains(target))
                closeFavPopover();
            if (ntfRef.current && !ntfRef.current.contains(target))
                setNtfOpen(false);
            if (accountMenuRef.current && !accountMenuRef.current.contains(target))
                setAccountMenuOpen(false);
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    useEffect(() => {
        return () => {
            if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
            if (markReadToastTimerRef.current) clearTimeout(markReadToastTimerRef.current);
        };
    }, []);

    // ntfItems 也用 useMemo，主要是 href 需要 langHref 处理
    const ntfItems = useMemo(() => [
        {
            type: "favorite",
            unread: true,
            icon: "♥",
            iconClass: "favorite",
            title: { from: "Aminata Diallo", text: "favorited your 2021 Toyota Corolla" },
            desc: "The buyer is interested in purchasing. You can reach out to close the deal.",
            time: "2h ago",
            href: langHref("/account#favorites"),
        },
        {
            type: "favorite",
            unread: true,
            icon: "♥",
            iconClass: "favorite",
            title: { from: "Moussa Camara", text: "favorited your 2019 Honda CR-V" },
            desc: "The buyer saved your listing — they have purchase intent.",
            time: "5h ago",
            href: langHref("/account#favorites"),
        },
        {
            type: "message",
            unread: true,
            icon: "💬",
            iconClass: "message",
            title: { from: "Moussa Diallo", text: "sent you a message" },
            desc: "Hello, is this Corolla still for sale? I'd like to schedule a test drive.",
            time: "10m ago",
            href: langHref("/account#inquiries"),
         },
         {
            type: "system",
            unread: false,
            icon: "⚙",
            iconClass: "system",
            title: { from: "", text: "Ad approved" },
            desc: "Your ad \"2020 Hyundai Tucson\" has been approved and is now live.",
            time: "Yesterday",
            href: langHref("/account#ads"),
        },
    ], [langHref]);

    const visibleNtf = ntfItems.filter((n) => n.type === ntfTab);

    const handleLogout = async () => {
        setLogoutLoading(true);
        try {
            const token = getToken();
            const res = await fetch("/auth/logout", {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });
            const json = await res.json();
            const ok = json && (json.code === 200 || json.code === 0);
            if (!ok) {
                alert(json?.message || t("header.logoutFail"));
                setLogoutLoading(false);
                return;
            }
        } catch (_a) {
            alert(t("common.networkError"));
            setLogoutLoading(false);
            return;
        }
        clearToken();
        setLogoutConfirmOpen(false);
        setLogoutLoading(false);
        setAccountMenuOpen(false);
        if (pathname === langHref("/account")) {
            router.push(langHref("/"));
        } else {
            window.location.reload();
        }
    };

    // 语言切换：保留当前路径，切换语言前缀
    const handleLangChange = (targetLang) => {
        const currentPrefix = params?.lang || DEFAULT_LANG;
        let stripped = pathname || "/";
        if (stripped.startsWith(`/${currentPrefix}/`)) {
            stripped = stripped.slice(`/${currentPrefix}`.length);
        } else if (stripped === `/${currentPrefix}`) {
            stripped = "/";
        }
        const newPrefix = LANG_PREFIX[targetLang] || "";
        router.push((newPrefix + stripped) || "/");
        setLangOpen(false);
    };

    const currentLangFlag = languages.find((l) => l.code === lang)?.flag;

    // ===== search suggest =====
    const handleSearchInput = (val) => {
      setSearchVal(val);
      setSuggestActiveIdx(-1);
      if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
      const kw = val.trim();
      if (!kw) { setSuggestions([]); setShowSuggest(false); return; }
      suggestTimerRef.current = setTimeout(async () => {
        try {
          const res = await listingApi.searchSuggest(kw, 10);
          const arr = Array.isArray(res) ? res : (res?.data || []);
          setSuggestions(arr);
          setShowSuggest(arr.length > 0);
        } catch { /* ignore */ }
      }, 200);
    };
    const handleSearchKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSuggestActiveIdx(prev => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSuggestActiveIdx(prev => Math.max(prev - 1, -1));
      } else if (e.key === 'Enter') {
        if (showSuggest && suggestActiveIdx >= 0 && suggestions[suggestActiveIdx]) {
          e.preventDefault();
          const s = suggestions[suggestActiveIdx];
          const q = typeof s === 'string' ? s : (s.keyword || s.label || '');
          setSearchVal(q); setShowSuggest(false);
          router.push(langHref('/search?q=' + encodeURIComponent(q)));
        } else if (searchVal.trim()) {
          e.preventDefault();
          setShowSuggest(false);
          router.push(langHref('/search?q=' + encodeURIComponent(searchVal.trim())));
        }
      } else if (e.key === 'Escape') {
        setShowSuggest(false);
      }
    };
    useEffect(() => {
      if (!showSuggest) return;
      const onDocClick = (e) => {
        if (searchBarRef.current && !searchBarRef.current.contains(e.target)) {
          setShowSuggest(false);
        }
      };
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [showSuggest]);

    return (<header className="header">
      <div className="container header-inner">
        <Link href={langHref("/")} className="logo">
          <img src="/assets/logo-mark.svg" alt="GuineeMall" className="logo-mark-img" />
          <span className="logo-text">GuineeMall</span>
        </Link>

        <nav className="nav">
          {navLinks.map((link) => {
            const homeHref = langHref("/");
            const isActive = pathname === link.href || (link.href !== homeHref && Boolean(pathname === null || pathname === void 0 ? void 0 : pathname.startsWith(link.href + "/")));
            return (<Link key={link.href} href={link.href} className={cn(isActive ? "active" : "")}>
              {link.label}
            </Link>);
          })}
        </nav>

        <div className="search-bar" ref={searchBarRef}
             onFocus={(e) => {
               if (lang === "fr" && !e.target.classList.contains("search-popout-input")) {
                 const popoutInput = e.currentTarget.querySelector(".search-popout-input");
                 if (popoutInput && document.activeElement !== popoutInput) {
                   popoutInput.focus();
                 }
               }
             }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder={t("header.searchPlaceholder")}
            value={searchVal}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            onFocus={() => searchVal.trim() && suggestions.length && setShowSuggest(true)}
          />
          {lang === "fr" && (
            <div className="search-popout">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                className="search-popout-input"
                type="text"
                placeholder={t("header.searchPlaceholder")}
                value={searchVal}
                onChange={(e) => handleSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => searchVal.trim() && suggestions.length && setShowSuggest(true)}
              />
            </div>
          )}
          {showSuggest && suggestions.length > 0 && searchVal.trim() && (
            <ul className="search-suggest-dropdown" role="listbox">
              {suggestions.map((s, i) => (
                <li
                  key={i}
                  role="option"
                  aria-selected={i === suggestActiveIdx}
                  className={cn("search-suggest-item", i === suggestActiveIdx && "active")}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const q = typeof s === "string" ? s : (s.keyword || s.label || "");
                    setSearchVal(q);
                    setShowSuggest(false);
                    router.push(langHref("/search?q=" + encodeURIComponent(q)));
                  }}
                  onMouseEnter={() => setSuggestActiveIdx(i)}
                >
                  <span>{typeof s === "string" ? s : (s.keyword || s.label || "")}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="header-actions">
          <div ref={langRef} className={cn("lang-dropdown", langOpen && "open")}>
            <button className="lang-switch" type="button" onClick={() => setLangOpen(!langOpen)} aria-haspopup="menu" aria-expanded={langOpen}>
              <span className="lang-label">🌐 {currentLangFlag}</span>
              <svg className="lang-caret" viewBox="0 0 12 8" width={10} height={6} fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M1 1l5 5 5-5"/>
              </svg>
            </button>
            <ul className="lang-menu" role="menu">
              {languages.map((item) => (<li key={item.code} className={cn("lang-option", lang === item.code && "selected")} role="menuitem" onClick={() => handleLangChange(item.code)}>
                  <span className="lang-flag">{item.flag}</span>
                  <span className="lang-name">{item.name}</span>
                  <span className="lang-check">✓</span>
                </li>))}
            </ul>
          </div>

          {isLoggedIn && (
              <div ref={favRef} className={cn("header-popover", favOpen && "open")}>
                <button className="icon-btn popover-trigger" type="button" onClick={() => {
                if (favOpen) closeFavPopover(); else setFavOpen(true);
                setNtfOpen(false);
            }} aria-label={t("header.favorites")}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={20} height={20}>
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  {badgeCount > 0 && (
                    <span className="badge-count" data-count={badgeCount > 99 ? "99+" : String(badgeCount)}>
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </button>
                <div className="popover-panel">
                  <div className="popover-header">
                    <span className="popover-title">{t("header.favorites")}</span>
                    <button className="popover-mark-read" onClick={handleMarkAllRead} disabled={markReadLoading}>
                      {markReadLoading ? t("common.loading") : t("header.markAllRead")}
                    </button>
                  </div>
                  {markReadToast && (
                    <div className="popover-toast">{markReadToast}</div>
                  )}
                  <div ref={favBodyRef} className="popover-body" style={{ maxHeight: 340, overflowY: "auto" }} onScroll={handleFavBodyScroll}>
                    {favListLoading && favPage === 1 ? (
                      <div style={{ padding: "40px 16px", textAlign: "center" }}>
                        <svg width={32} height={32} viewBox="0 0 24 24" style={{ margin: "0 auto 12px", display: "block" }}>
                          <circle cx="12" cy="12" r="10" fill="none" stroke="var(--color-border)" strokeWidth="3" />
                          <path d="M12 2 a10 10 0 0 1 10 10" fill="none" stroke="var(--color-brand)" strokeWidth="3" strokeLinecap="round">
                            <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
                          </path>
                        </svg>
                        <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>{t("common.loading")}</div>
                      </div>
                    ) : favRecords.length === 0 ? (
                      <div style={{ padding: "40px 16px", textAlign: "center", fontSize: 13, color: "var(--color-text-muted)" }}>{t("header.noFavorites")}</div>
                    ) : (
                      <>
                      {favRecords.map((fav, i) => {
                        const userName = fav.userName || t("common.unknown");
                        const listingTitle = fav.listingTitle || "";
                        const createTime = fav.createTime ? fav.createTime.split("T")[0] : "";
                        return (
                          <Link key={fav.id || i} className="fav-item" href={langHref("/account?tab=favoritedMe")} onClick={() => closeFavPopover()}>
                            {fav.userAvatar ? (
                              <img src={fav.userAvatar} alt="" className="fav-avatar" style={{ objectFit: "cover", background: "#e5e7eb" }} />
                            ) : (
                              <div className="fav-avatar" style={{ background: `hsl(${(userName.charCodeAt(0) * 37) % 360}, 55%, 55%)` }}>
                                {userName.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="fav-info">
                              <div className="fav-name">{userName}</div>
                              <div className="fav-sub">
                                {t("header.favoritedYou")} <span className="fav-car">{listingTitle}</span>
                              </div>
                              <div className="fav-meta">{createTime}</div>
                            </div>
                            {fav.coverUrl ? (
                              <img src={fav.coverUrl} alt="" className="fav-thumb" style={{ objectFit: "cover" }} />
                            ) : (
                              <div className="fav-thumb" style={{ background: "linear-gradient(135deg,#e5e7eb,#f3f4f6)" }} />
                            )}
                          </Link>
                        );
                      })}
                      {favListLoadingMore && (
                        <div style={{ padding: "10px 16px", textAlign: "center", fontSize: 12, color: "var(--color-text-muted)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                          <svg width={14} height={14} viewBox="0 0 24 24" style={{ display: "block" }}>
                            <circle cx="12" cy="12" r="10" fill="none" stroke="var(--color-border)" strokeWidth="3" />
                            <path d="M12 2 a10 10 0 0 1 10 10" fill="none" stroke="var(--color-brand)" strokeWidth="3" strokeLinecap="round">
                              <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
                            </path>
                          </svg>
                          {t("common.loading")}
                        </div>
                      )}
                      {!favListHasMore && favRecords.length > 0 && (
                        <div style={{ padding: "6px 16px 10px", textAlign: "center", fontSize: 12, color: "var(--color-text-muted)" }}>{t("common.noMore")}</div>
                      )}
                      </>
                    )}
                  </div>
                  <div className="popover-footer">
                    <Link href={langHref("/account?tab=favoritedMe")} className="popover-link" onClick={() => closeFavPopover()}>
                      {t("header.viewWhoFavoritedMe")}
                    </Link>
                  </div>
                </div>
              </div>
          )}

          {isLoggedIn && (
          <div ref={ntfRef} className={cn("header-popover", ntfOpen && "open")}>
            <button className="icon-btn popover-trigger" type="button" onClick={() => {
            setNtfOpen(!ntfOpen);
            closeFavPopover();
        }} aria-label={t("header.notifications")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={20} height={20}>
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              {ntfBadge > 0 && (
                <span className="badge-count" data-count={ntfBadge > 99 ? "99+" : String(ntfBadge)}>
                  {ntfBadge > 99 ? "99+" : ntfBadge}
                </span>
              )}
            </button>
            <div className="popover-panel">
              <div className="popover-header">
                <span className="popover-title">{t("header.notifications")}</span>
                <button className="popover-mark-read">{t("header.markAllRead")}</button>
              </div>
              <div className="ntf-tabs">
                {[
             { key: "message", label: t("header.newMessage") },
             { key: "system", label: t("header.system") },
         ].map((tab) => (<button key={tab.key} className={cn("ntf-tab", ntfTab === tab.key && "active")} onClick={() => setNtfTab(tab.key)}>
                    {tab.label}
                  </button>))}
              </div>
              <div className={cn("popover-body", "ntf-body")} ref={ntfBodyRef} onScroll={handleNtfBodyScroll}>
                {ntfTab === "system" ? (
                  ntfSystemLoading ? (
                    <div style={{ padding: "40px 16px", textAlign: "center" }}>
                      <svg width={28} height={28} viewBox="0 0 24 24" style={{ margin: "0 auto 10px", display: "block" }}>
                        <circle cx="12" cy="12" r="10" fill="none" stroke="var(--color-border)" strokeWidth="3" />
                        <path d="M12 2 a10 10 0 0 1 10 10" fill="none" stroke="var(--color-brand)" strokeWidth="3" strokeLinecap="round">
                          <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
                        </path>
                      </svg>
                      <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>{t("common.loading")}</div>
                    </div>
                  ) : ntfSystemList.length === 0 ? (
                    <div style={{ padding: "40px 16px", textAlign: "center", fontSize: 13, color: "var(--color-text-muted)" }}>{t("header.noSystemNotifications")}</div>
                  ) : (
                    <>
                      {ntfSystemList.map((n) => (
                        <div key={n.id} className={cn("ntf-item", "ntf-system-item", n.isRead === 0 && "unread")}>
                          <div className="ntf-icon system">
                            <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
                              <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                            </svg>
                          </div>
                          <div className="ntf-content">
                            <div className="ntf-title">{getNotificationTitle(n)}</div>
                            {getNotificationContent(n) && <div className="ntf-text">{getNotificationContent(n)}</div>}
                            {n.createTime && <div className="ntf-time">{formatDateTime(n.createTime)}</div>}
                          </div>
                        </div>
                      ))}
                      {ntfSystemLoadingMore && (
                        <div className="ntf-load-more">
                          <svg width={18} height={18} viewBox="0 0 24 24">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="var(--color-border)" strokeWidth="3" />
                            <path d="M12 2 a10 10 0 0 1 10 10" fill="none" stroke="var(--color-brand)" strokeWidth="3" strokeLinecap="round">
                              <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
                            </path>
                          </svg>
                          <span>{t("common.loading")}</span>
                        </div>
                      )}
                      {!ntfSystemHasMore && ntfSystemList.length > 0 && (
                        <div className="ntf-load-end">{t("common.noMore")}</div>
                      )}
                    </>
                  )
                ) : ntfTab === "message" ? (
                  ntfMsgLoading ? (
                    <div style={{ padding: "40px 16px", textAlign: "center" }}>
                      <svg width={28} height={28} viewBox="0 0 24 24" style={{ margin: "0 auto 10px", display: "block" }}>
                        <circle cx="12" cy="12" r="10" fill="none" stroke="var(--color-border)" strokeWidth="3" />
                        <path d="M12 2 a10 10 0 0 1 10 10" fill="none" stroke="var(--color-brand)" strokeWidth="3" strokeLinecap="round">
                          <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
                        </path>
                      </svg>
                      <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>{t("common.loading")}</div>
                    </div>
                  ) : ntfMsgArr.length === 0 ? (
                    <div style={{ padding: "40px 16px", textAlign: "center", fontSize: 13, color: "var(--color-text-muted)" }}>{t("header.noMessages")}</div>
                  ) : (
                    <>
                      {ntfMsgArr.map((c) => (
                        <Link key={c.otherUserId} href={langHref("/account?tab=messages")} className={cn("ntf-item", "ntf-msg-item", !c.isRead && "unread")}>
                          <div className="ntf-icon message">
                            {c.otherAvatar ? (
                              <img src={c.otherAvatar} alt="" width={38} height={38} style={{ borderRadius: "50%", objectFit: "cover" }} />
                            ) : (
                              <span style={{ fontSize: 15, fontWeight: 600 }}>{(c.otherNickname || "?").charAt(0)}</span>
                            )}
                          </div>
                          <div className="ntf-content">
                            <div className="ntf-title">
                              <span className="ntf-from">{c.otherNickname || t("common.unknown")}</span>
                              {c.unreadCount > 0 && <span className="ntf-badge">{c.unreadCount}</span>}
                            </div>
                            {c.listingTitle && <div className="ntf-listing-title">{t("header.aboutListing")}{c.listingTitle}</div>}
                            {c.lastMessage && <div className="ntf-text">{unescapeText(c.lastMessage)}</div>}
                            {c.lastTime && <div className="ntf-time">{formatDateTime(c.lastTime)}</div>}
                          </div>
                        </Link>
                      ))}
                    </>
                  )
                ) : (
                  visibleNtf.map((n, i) => (<Link key={i} className={cn("ntf-item", n.unread && "unread")} data-type={n.type} href={n.href}>
                    <div className="ntf-icon" style={{ color: n.iconClass === "favorite" ? "var(--color-brand)" : undefined }} dangerouslySetInnerHTML={{ __html: n.icon }}/>
                    <div className="ntf-content">
                      <div className="ntf-title">
                        {n.title.from && <span className="ntf-from">{n.title.from}</span>} {n.title.text}
                      </div>
                      <div className="ntf-text">{n.desc}</div>
                      <div className="ntf-time">{n.time}</div>
                    </div>
                  </Link>))
                )}
              </div>
              <div className="popover-footer">
                <Link href={langHref("/account")} className="popover-link" onClick={(e) => { e.preventDefault(); setNtfOpen(false); window.location.href = langHref("/account"); }}>
                  {t("header.viewAllNotifications")}
                </Link>
              </div>
            </div>
          </div>
          )}

          {isLoggedIn && (
          <Link href={langHref("/publish")} className="btn btn-primary">
            {t("header.postAd")}
          </Link>
          )}

          {isLoggedIn ? (
            <div
              ref={accountMenuRef}
              style={{ position: "relative", display: "inline-flex" }}
              onMouseEnter={() => {
                if (closeTimerRef.current) {
                  clearTimeout(closeTimerRef.current);
                  closeTimerRef.current = null;
                }
                setAccountMenuOpen(true);
              }}
              onMouseLeave={() => {
                closeTimerRef.current = setTimeout(() => {
                  setAccountMenuOpen(false);
                }, 200);
              }}
            >
              <button
                className="icon-btn"
                aria-label={t("header.account")}
                style={{
                  cursor: "pointer",
                  position: "relative",
                  transition: "all 0.25s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "var(--color-brand)";
                  e.currentTarget.style.transform = "scale(1.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "";
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} width={21} height={21}>
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="7" r="4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {/* 下拉菜单 */}
              {accountMenuOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 12px)",
                    right: -8,
                    minWidth: 180,
                    background: "rgba(255,255,255,0.96)",
                    backdropFilter: "blur(16px) saturate(140%)",
                    WebkitBackdropFilter: "blur(16px) saturate(140%)",
                    borderRadius: 14,
                    border: "0.5px solid rgba(229, 233, 229, 0.5)",
                    boxShadow: "0 12px 40px rgba(15, 23, 42, 0.10), 0 4px 14px rgba(15, 23, 42, 0.06), 0 0 0 0.5px rgba(255,255,255,0.8) inset",
                    padding: "5px 0",
                    zIndex: 999,
                    animation: "accountMenuFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)",
                    transformOrigin: "top right",
                  }}
                  onMouseEnter={() => {
                    if (closeTimerRef.current) {
                      clearTimeout(closeTimerRef.current);
                      closeTimerRef.current = null;
                    }
                  }}
                  onMouseLeave={() => {
                    closeTimerRef.current = setTimeout(() => {
                      setAccountMenuOpen(false);
                    }, 150);
                  }}
                >
                  {/* 三角指针 */}
                  <div
                    style={{
                      position: "absolute",
                      top: -5,
                      right: 14,
                      width: 10,
                      height: 10,
                      background: "rgba(255,255,255,0.96)",
                      transform: "rotate(45deg)",
                      borderLeft: "0.5px solid rgba(229, 233, 229, 0.5)",
                      borderTop: "0.5px solid rgba(229, 233, 229, 0.5)",
                      borderTopLeftRadius: 3,
                    }}
                  />

                  {/* 个人中心 */}
                  <Link
                    href={langHref("/account")}
                    onClick={() => setAccountMenuOpen(false)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 11,
                      padding: "10px 18px",
                      margin: "2px 6px",
                      fontSize: 13.5,
                      color: "var(--color-text)",
                      textDecoration: "none",
                      borderRadius: 8,
                      transition: "all 0.15s ease",
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--color-bg-alt, #F4F6F4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      width="16"
                      height="16"
                      style={{ flexShrink: 0, color: "var(--color-text-muted)", transition: "color 0.15s ease" }}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    <span>{t("account.title")}</span>
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 10,
                        color: "var(--color-text-muted)",
                        background: "var(--color-bg-alt, #F4F6F4)",
                        padding: "1px 7px",
                        borderRadius: 4,
                        lineHeight: "18px",
                      }}
                    >
                      {t("account.tabs.info")}
                    </span>
                  </Link>

                  {/* 分隔线 */}
                  <div
                    style={{
                      height: 1,
                      margin: "3px 14px",
                      background: "linear-gradient(to right, transparent, var(--color-border, #E5E9E5), transparent)",
                    }}
                  />

                  {/* 退出登录 */}
                  <button
                    type="button"
                    onClick={() => {
                      setAccountMenuOpen(false);
                      setLogoutConfirmOpen(true);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 11,
                      padding: "10px 18px",
                      margin: "2px 6px",
                      fontSize: 13.5,
                      color: "#E53E3E",
                      background: "transparent",
                      border: "none",
                      width: "calc(100% - 12px)",
                      textAlign: "left",
                      cursor: "pointer",
                      borderRadius: 8,
                      transition: "all 0.15s ease",
                      fontWeight: 500,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--color-bg-alt, #F4F6F4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      width="16"
                      height="16"
                      style={{ flexShrink: 0, transition: "transform 0.2s ease" }}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    <span>{t("header.logout")}</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setLoginModalOpen(true)}
              aria-label={t("header.login")}
              className="header-login-link"
              style={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 4px 8px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 500,
                color: "var(--color-text)",
                fontFamily: "inherit",
                letterSpacing: "0.01em",
                lineHeight: 1,
                transition: "color 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = "var(--color-brand)";
                const underline = e.currentTarget.querySelector(".header-login-underline");
                if (underline) underline.style.transform = "scaleX(1)";
                const icon = e.currentTarget.querySelector(".header-login-icon");
                if (icon) icon.style.transform = "translateX(2px)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "var(--color-text)";
                const underline = e.currentTarget.querySelector(".header-login-underline");
                if (underline) underline.style.transform = "scaleX(0)";
                const icon = e.currentTarget.querySelector(".header-login-icon");
                if (icon) icon.style.transform = "translateX(0)";
              }}
            >
              <span
                className="header-login-icon"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="16" height="16">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" strokeLinecap="round" strokeLinejoin="round" />
                  <polyline points="10 17 15 12 10 7" strokeLinecap="round" strokeLinejoin="round" />
                  <line x1="15" y1="12" x2="3" y2="12" strokeLinecap="round" />
                </svg>
              </span>
              <span style={{ position: "relative", paddingBottom: 2 }}>
                {t("header.login")}
                <span
                  className="header-login-underline"
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: -3,
                    height: 1.5,
                    background: "var(--color-brand)",
                    borderRadius: 2,
                    transform: "scaleX(0)",
                    transformOrigin: "left center",
                    transition: "transform 0.32s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
              </span>
            </button>
          )}

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="hamburger-btn"
            aria-label={t("common.menu")}
            aria-expanded={mobileOpen}
          >
            <span/><span/><span/>
          </button>
        </div>

        {/* ── Mobile drawer ── */}
        {mobileOpen && (
          <>
            <div className="mobile-drawer-backdrop" onClick={() => setMobileOpen(false)} />
            <aside className="mobile-drawer">
              <div className="mobile-drawer-header">
                <span style={{ fontSize: 18, fontWeight: 700, color: "var(--color-primary)" }}>GuineeMall</span>
                <button onClick={() => setMobileOpen(false)} className="mobile-drawer-close" aria-label="Close">✕</button>
              </div>

              {/* Nav links */}
              <nav className="mobile-drawer-nav">
                {navLinks.map((link) => {
                  const homeHref = langHref("/");
                  const isActive = pathname === link.href || (link.href !== homeHref && Boolean(pathname === null || pathname === void 0 ? void 0 : pathname.startsWith(link.href + "/")));
                  return (<Link key={link.href} href={link.href}
                    className={cn("mobile-drawer-link", isActive && "active")}
                    onClick={() => setMobileOpen(false)}
                  >{link.label}</Link>);
                })}
              </nav>

              {/* Language switch */}
              <div className="mobile-drawer-section">
                <div className="mobile-drawer-section-title">🌐 {t("header.language") || "Langue"}</div>
                <div className="mobile-lang-list">
                  {languages.map((item) => (
                    <button key={item.code}
                      className={cn("mobile-lang-btn", lang === item.code && "selected")}
                      onClick={() => handleLangChange(item.code)}>
                      <span className="lang-flag">{item.flag}</span>
                      <span>{item.name}</span>
                      {lang === item.code && <span className="lang-check">✓</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Auth / CTA */}
              <div className="mobile-drawer-footer">
                {isLoggedIn ? (
                  <>
                    <Link href={langHref("/account")} className="mobile-drawer-cta secondary" onClick={() => setMobileOpen(false)}>
                      {t("header.myAccount")}
                    </Link>
                    <Link href={langHref("/publish")} className="mobile-drawer-cta primary" onClick={() => setMobileOpen(false)}>
                      🚀 {t("header.postAd")}
                    </Link>
                  </>
                ) : (
                  <>
                    <button className="mobile-drawer-cta secondary"
                      onClick={() => { setLoginModalOpen(true); setMobileOpen(false); }}>
                      {t("header.login")}
                    </button>
                    <Link href={langHref("/sign_up")} className="mobile-drawer-cta primary" onClick={() => setMobileOpen(false)}>
                      {t("header.signUp")}
                    </Link>
                  </>
                )}
              </div>
            </aside>
          </>
        )}
      </div>
      <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />

      {/* 退出确认弹窗 */}
      {logoutConfirmOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.4)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "fadeIn 0.18s ease",
          }}
          onClick={() => setLogoutConfirmOpen(false)}
        >
          <div
            style={{
              width: 380,
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 20px 60px rgba(15, 23, 42, 0.25)",
              padding: 24,
              animation: "zoomIn 0.22s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: "rgba(229, 62, 62, 0.08)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="#E53E3E" strokeWidth={2} width={24} height={24}>
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="16 17 21 12 16 7" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="21" y1="12" x2="9" y2="12" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: "var(--color-text)",
                  marginBottom: 6,
                }}
              >
                {t("account.logoutConfirmTitle")}
              </h3>
              <p
                style={{
                  fontSize: 14,
                  color: "var(--color-text-secondary)",
                  margin: 0,
                }}
              >
                {t("account.logoutConfirmDesc")}
              </p>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 24 }}>
              <button
                type="button"
                onClick={() => setLogoutConfirmOpen(false)}
                disabled={logoutLoading}
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: 10,
                  border: "1px solid var(--color-border)",
                  background: "transparent",
                  color: "var(--color-text)",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: logoutLoading ? "not-allowed" : "pointer",
                  transition: "all 0.18s ease",
                }}
                onMouseEnter={(e) => {
                  if (!logoutLoading) e.currentTarget.style.background = "var(--color-bg-alt)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={handleLogout}
                disabled={logoutLoading}
                style={{
                  flex: 1,
                  height: 44,
                  borderRadius: 10,
                  border: "none",
                  background: "#E53E3E",
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: logoutLoading ? "not-allowed" : "pointer",
                  transition: "all 0.18s ease",
                  opacity: logoutLoading ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!logoutLoading) e.currentTarget.style.background = "#C53030";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#E53E3E";
                }}
              >
                {logoutLoading ? t("common.loading") : t("account.confirmLogout")}
              </button>
            </div>
          </div>
        </div>
      )}
      </header>);
}
